import { useCallback, useEffect, useState } from 'react';
import { NO_STYLE } from '@hienbet/shared';
import { ApiError, formatChips, loginUrl, type Me } from '../api.js';
import { PlayerName } from '../components/PlayerName.js';

/**
 * Live preview of a cosmetic, worn by the viewer's own name.
 *
 * "Jade name, 25,000 chips" is an ask; seeing YOUR name in jade is a want.
 * Every preview renders through the same PlayerName component the leaderboard
 * and ticker use, so what is shown here is exactly what they get.
 */
function Preview({ id, name }: { id: string; name: string }) {
  const dot = id.indexOf('.');
  const kind = dot === -1 ? id : id.slice(0, dot);
  const value = dot === -1 ? '' : id.slice(dot + 1);

  switch (kind) {
    case 'color':
      return (
        <span className="shop-preview">
          <PlayerName name={name} style={{ ...NO_STYLE, color: value }} />
        </span>
      );
    case 'effect':
      return (
        <span className="shop-preview">
          <PlayerName name={name} style={{ ...NO_STYLE, effect: value }} />
        </span>
      );
    case 'title':
      return (
        <span className="shop-preview">
          <PlayerName name={name} style={{ ...NO_STYLE, title: value }} showTitle />
        </span>
      );
    case 'ticket':
      // A miniature gilded ticket — the private flex.
      return (
        <span className="shop-preview">
          <span className="gilded-ticket-preview">
            <span className="gtp-sel">WINS</span>
            <span className="gtp-amt num">+9,040</span>
          </span>
        </span>
      );
    case 'flair':
      if (value === 'founder') {
        return (
          <span className="shop-preview">
            <span className="founder-preview">
              <span className="founder-name">{name}</span>
              <span className="founder-date">est. today</span>
            </span>
          </span>
        );
      }
      if (value === 'egg') {
        return (
          <span className="shop-preview">
            <PlayerName name={name} style={{ ...NO_STYLE, egg: true }} />
          </span>
        );
      }
      if (value === 'spotlight') {
        return (
          <span className="shop-preview shop-preview-row highlighted-preview">
            <span className="num" style={{ color: 'var(--ink-faint)' }}>4</span>
            <PlayerName name={name} />
            <span className="num" style={{ color: 'var(--money)' }}>128,400</span>
          </span>
        );
      }
      if (value === 'signature') {
        // A miniature of the on-stream moment this buys.
        return (
          <span className="shop-preview">
            <span className="signature-card-preview">
              <span className="sig-who">{name}</span>
              <span className="sig-verb">won</span>
              <span className="sig-amount">48,200</span>
            </span>
          </span>
        );
      }
      return null;
    default:
      return null;
  }
}

interface CatalogItem {
  id: string;
  title: string;
  description: string;
  chipCost: number;
  kind: 'auto' | 'manual';
  stockPerStream: number | null;
}

interface Owned {
  cosmetic: string;
  value: string;
}

const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('hienbet.session');
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const problem = body as { error?: string; message?: string };
    throw new ApiError(problem.message ?? 'That did not work.', problem.error ?? 'error');
  }
  return body as T;
}

/**
 * Where winnings go.
 *
 * Chips only ever flow one way — channel points buy them, this spends them, and
 * nothing converts back. Without somewhere to spend, balances only climb and the
 * stakes stop meaning anything.
 */
export function RewardsPage({ me, onBalance }: { me: Me | null; onBalance: () => void }) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [owned, setOwned] = useState<Owned[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; bad: boolean } | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await call<{ rewards: CatalogItem[]; balance: number | null; owned: Owned[] }>(
        '/api/rewards',
      );
      setItems(data.rewards);
      setOwned(data.owned);
      setBalance(data.balance);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const ownsIt = (id: string) => {
    const [cosmetic, value] = id.split(/\.(.*)/);
    return owned.some((o) => o.cosmetic === cosmetic && o.value === value);
  };

  const redeem = async (item: CatalogItem) => {
    setBusy(item.id);
    setMessage(null);
    try {
      await call('/api/rewards/redeem', {
        method: 'POST',
        body: JSON.stringify({ rewardId: item.id, note: item.kind === 'manual' ? note : '' }),
      });
      setMessage({
        text:
          item.kind === 'auto'
            ? `${item.title} is yours.`
            : `${item.title} is in the queue — she'll do it on stream.`,
        bad: false,
      });
      setNote('');
      await load();
      onBalance();
    } catch (err) {
      setMessage({ text: err instanceof ApiError ? err.message : 'That did not work.', bad: true });
    } finally {
      setBusy(null);
    }
  };

  const cosmetics = items?.filter((i) => i.kind === 'auto') ?? [];
  const onStream = items?.filter((i) => i.kind === 'manual') ?? [];
  const chips = balance ?? me?.balance ?? 0;
  const previewName = me?.user?.displayName || 'YourName';

  return (
    <div className="page">
      <header className="page-head">
        <h1>Spend your chips</h1>
        <p>
          Chips only go one way — points buy them, this spends them, nothing converts back. If
          everyone just hoarded, none of the bets would mean anything.
        </p>
      </header>

      {message && <p className={message.bad ? 'slip-error' : 'slip-done'}>{message.text}</p>}

      {items === null && <p className="note">Loading…</p>}

      {cosmetics.length > 0 && (
        <>
          <h2 className="section">Instant</h2>
          <div className="shop">
            {cosmetics.map((item) => {
              const have = ownsIt(item.id);
              const canAfford = chips >= item.chipCost;
              return (
                <div className="shop-item" key={item.id}>
                  <Preview id={item.id} name={previewName} />
                  <div className="shop-text">
                    <div className="shop-title">{item.title}</div>
                    <div className="shop-desc">{item.description}</div>
                  </div>
                  {me?.loggedIn ? (
                    <button
                      className="shop-buy"
                      disabled={have || !canAfford || busy === item.id}
                      onClick={() => void redeem(item)}
                    >
                      {have ? 'owned' : busy === item.id ? '…' : formatChips(item.chipCost)}
                    </button>
                  ) : (
                    <span className="shop-price num">{formatChips(item.chipCost)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {onStream.length > 0 && (
        <>
          <h2 className="section">On stream</h2>
          <p className="note shop-note">
            These are things Hien has to actually do, so there are only a few per stream and they
            cost accordingly. She can decline anything — you get every chip back if she does.
          </p>

          {me?.loggedIn && (
            <input
              className="shop-note-input"
              placeholder="Anything she should know? (optional)"
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
            />
          )}

          <div className="shop">
            {onStream.map((item) => (
              <div className="shop-item" key={item.id}>
                <div className="shop-text">
                  <div className="shop-title">{item.title}</div>
                  <div className="shop-desc">
                    {item.description}
                    {item.stockPerStream !== null && (
                      <em className="shop-stock"> · {item.stockPerStream} per stream</em>
                    )}
                  </div>
                </div>
                {me?.loggedIn ? (
                  <button
                    className="shop-buy"
                    disabled={chips < item.chipCost || busy === item.id}
                    onClick={() => void redeem(item)}
                  >
                    {busy === item.id ? '…' : formatChips(item.chipCost)}
                  </button>
                ) : (
                  <span className="shop-price num">{formatChips(item.chipCost)}</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {!me?.loggedIn && items !== null && (
        <a className="place as-link inline" href={loginUrl()}>
          Log in with Twitch to spend
        </a>
      )}

      <p className="shop-fine">
        Everything here is cosmetic or something Hien does on stream. Nothing has cash value,
        nothing can be sold, and nothing can be turned back into channel points.
      </p>
    </div>
  );
}
